import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './GlassCapsulePopup.css';

interface GlassCapsulePopupProps {
    message: string;
    icon?: string;
    show: boolean;
    onClose?: () => void;
    autoHideDuration?: number; // milliseconds, 0 means no auto-hide
    showGetVerifiedButton?: boolean;
    className?: string;
}

const GlassCapsulePopup: React.FC<GlassCapsulePopupProps> = ({ 
    message, 
    icon, 
    show, 
    onClose,
    autoHideDuration = 2000,
    showGetVerifiedButton = false,
    className = ''
}) => {
    const [isVisible, setIsVisible] = useState(show);
    const [isFadingOut, setIsFadingOut] = useState(false);


    // Handle show/hide with fade animation
    useEffect(() => {
        if (show) {
            setIsVisible(true);
            setIsFadingOut(false);
        } else if (isVisible) {
            setIsFadingOut(true);
            setTimeout(() => {
                setIsVisible(false);
                setIsFadingOut(false);
            }, 300); // Match CSS animation duration
        }
    }, [show, isVisible]);

    // Auto-hide functionality
    useEffect(() => {
        if (isVisible && autoHideDuration > 0 && onClose) {
            const timer = setTimeout(() => {
                if (onClose) onClose();
            }, autoHideDuration);
            return () => clearTimeout(timer);
        }
    }, [isVisible, autoHideDuration, onClose]);

    if (!isVisible) return null;

    const popupContent = (
        <div className={`glass-capsule-popup ${className} ${isFadingOut ? 'fade-out' : ''}`} role="status" aria-live="polite">
            <div className="glass-capsule-content">
                {icon && <span className="glass-capsule-icon">{icon}</span>}
                <span className="glass-capsule-text">{message}</span>
            </div>
        </div>
    );

    // Render the popup as a portal to document.body to avoid container constraints
    return createPortal(popupContent, document.body);
};

export default GlassCapsulePopup;

